import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Check, X, Lightbulb, Briefcase, Wrench, Eye } from "lucide-react";
import { format } from "date-fns";
import {
  useAllTaxonomySuggestions,
  useReviewTaxonomySuggestion,
  SuggestionType,
  SuggestionStatus,
  TaxonomySuggestion,
} from "@/hooks/useTaxonomySuggestions";

export function TaxonomySuggestionsManager() {
  const [typeFilter, setTypeFilter] = useState<SuggestionType | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<SuggestionStatus | undefined>("pending");
  const [selectedSuggestion, setSelectedSuggestion] = useState<TaxonomySuggestion | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [finalLabel, setFinalLabel] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  const { data: suggestions, isLoading } = useAllTaxonomySuggestions({
    type: typeFilter,
    status: statusFilter,
  });

  const reviewMutation = useReviewTaxonomySuggestion();

  const handleApprove = async () => {
    if (!selectedSuggestion) return;
    try {
      await reviewMutation.mutateAsync({
        suggestionId: selectedSuggestion.id,
        action: "approve",
        finalLabel: finalLabel.trim() || undefined,
      });
      setApproveDialogOpen(false);
      setSelectedSuggestion(null);
      setFinalLabel("");
    } catch {
      // Error handled by mutation
    }
  };

  const handleReject = async () => {
    if (!selectedSuggestion) return;
    try {
      await reviewMutation.mutateAsync({
        suggestionId: selectedSuggestion.id,
        action: "reject",
        rejectionReason: rejectionReason.trim() || undefined,
      });
      setRejectDialogOpen(false);
      setSelectedSuggestion(null);
      setRejectionReason("");
    } catch {
      // Error handled by mutation
    }
  };

  const openApproveDialog = (suggestion: TaxonomySuggestion) => {
    setSelectedSuggestion(suggestion);
    setFinalLabel(suggestion.suggested_label);
    setApproveDialogOpen(true);
  };

  const openRejectDialog = (suggestion: TaxonomySuggestion) => {
    setSelectedSuggestion(suggestion);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const openViewDialog = (suggestion: TaxonomySuggestion) => {
    setSelectedSuggestion(suggestion);
    setViewDialogOpen(true);
  };

  const getStatusBadge = (status: SuggestionStatus) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-warning/10 text-warning border-warning">Pending</Badge>;
      case "approved":
        return <Badge variant="outline" className="bg-success/10 text-success border-success">Approved</Badge>;
      case "rejected":
        return <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive">Rejected</Badge>;
    }
  };

  const getTypeIcon = (type: SuggestionType) => {
    return type === "skill" ? (
      <Wrench className="h-4 w-4 text-primary" />
    ) : (
      <Briefcase className="h-4 w-4 text-secondary-foreground" />
    );
  };

  const pendingCount = suggestions?.filter(s => s.status === "pending").length || 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5" />
          Taxonomy Suggestions
          {pendingCount > 0 && (
            <Badge variant="destructive" className="ml-2">{pendingCount} pending</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Review user suggestions for new skills and industries
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Type</Label>
            <Tabs
              value={typeFilter || "all"}
              onValueChange={(v) => setTypeFilter(v === "all" ? undefined : (v as SuggestionType))}
            >
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="skill">Skills</TabsTrigger>
                <TabsTrigger value="industry">Industries</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select
              value={statusFilter || "all"}
              onValueChange={(v) => setStatusFilter(v === "all" ? undefined : (v as SuggestionStatus))}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !suggestions?.length ? (
          <div className="text-center py-8 text-muted-foreground">
            No suggestions found
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Suggestion</TableHead>
                  <TableHead>Suggested By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suggestions.map((suggestion) => (
                  <TableRow key={suggestion.id}>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {getTypeIcon(suggestion.suggestion_type)}
                        <span className="capitalize text-sm">
                          {suggestion.suggestion_type}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{suggestion.suggested_label}</div>
                      {suggestion.suggested_description && (
                        <p className="text-xs text-muted-foreground line-clamp-1">
                          {suggestion.suggested_description}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {suggestion.suggester_profile?.full_name || "Unknown"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(suggestion.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>{getStatusBadge(suggestion.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openViewDialog(suggestion)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {suggestion.status === "pending" && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-success hover:text-success hover:bg-success/10"
                              onClick={() => openApproveDialog(suggestion)}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => openRejectDialog(suggestion)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Approve Dialog */}
        <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Approve Suggestion</DialogTitle>
              <DialogDescription>
                This will add the item to the official{" "}
                {selectedSuggestion?.suggestion_type === "skill" ? "skills" : "industries"} list.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Final Label</Label>
                <Input
                  value={finalLabel}
                  onChange={(e) => setFinalLabel(e.target.value)}
                  placeholder="Edit the label if needed"
                />
                <p className="text-xs text-muted-foreground">
                  You can adjust capitalization or wording before adding.
                </p>
              </div>
              {selectedSuggestion?.suggested_description && (
                <div className="space-y-2">
                  <Label>User's Description</Label>
                  <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                    {selectedSuggestion.suggested_description}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setApproveDialogOpen(false)}
                disabled={reviewMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleApprove}
                disabled={!finalLabel.trim() || reviewMutation.isPending}
                className="bg-success hover:bg-success/90"
              >
                {reviewMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Approve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Suggestion</DialogTitle>
              <DialogDescription>
                This suggestion will be marked as rejected. Optionally provide a reason.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-md">
                <p className="font-medium">{selectedSuggestion?.suggested_label}</p>
                {selectedSuggestion?.suggested_description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedSuggestion.suggested_description}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Rejection Reason (Internal)</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Optional: Why was this rejected?"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  This reason is for admin reference only and won't be shown to users.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRejectDialogOpen(false)}
                disabled={reviewMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={reviewMutation.isPending}
              >
                {reviewMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <X className="mr-2 h-4 w-4" />
                )}
                Reject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedSuggestion && getTypeIcon(selectedSuggestion.suggestion_type)}
                Suggestion Details
              </DialogTitle>
            </DialogHeader>
            {selectedSuggestion && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Type</Label>
                    <p className="capitalize">{selectedSuggestion.suggestion_type}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <div className="mt-1">{getStatusBadge(selectedSuggestion.status)}</div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Suggested Label</Label>
                  <p className="font-medium">{selectedSuggestion.suggested_label}</p>
                </div>
                {selectedSuggestion.suggested_description && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Description</Label>
                    <p className="text-sm">{selectedSuggestion.suggested_description}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Suggested By</Label>
                    <p className="text-sm">
                      {selectedSuggestion.suggester_profile?.full_name || "Unknown"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Date</Label>
                    <p className="text-sm">
                      {format(new Date(selectedSuggestion.created_at), "PPp")}
                    </p>
                  </div>
                </div>
                {selectedSuggestion.status !== "pending" && (
                  <>
                    {selectedSuggestion.final_label && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Final Label (Approved)</Label>
                        <p className="text-sm">{selectedSuggestion.final_label}</p>
                      </div>
                    )}
                    {selectedSuggestion.rejection_reason && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Rejection Reason</Label>
                        <p className="text-sm">{selectedSuggestion.rejection_reason}</p>
                      </div>
                    )}
                    {selectedSuggestion.reviewed_at && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Reviewed At</Label>
                        <p className="text-sm">
                          {format(new Date(selectedSuggestion.reviewed_at), "PPp")}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                Close
              </Button>
              {selectedSuggestion?.status === "pending" && (
                <>
                  <Button
                    onClick={() => {
                      setViewDialogOpen(false);
                      openApproveDialog(selectedSuggestion);
                    }}
                    className="bg-success hover:bg-success/90"
                  >
                    <Check className="mr-2 h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      setViewDialogOpen(false);
                      openRejectDialog(selectedSuggestion);
                    }}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Reject
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
